import { Component, OnDestroy, Input, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import moment from 'moment';

@Component({
    selector: 'app-tachometer-chart',
    templateUrl: './tachometer.component.html',
    styleUrls: ['./tachometer.component.scss'],
    standalone: false
})

export class TachometerComponent implements OnDestroy, OnChanges, AfterViewInit {
  chartId: string = 'tachometer-chart-' + Math.random().toString(36).substring(2, 9);
  private chart: Highcharts.Chart | undefined;

  @Input() budgetData;
  @Input() budget: number;
  @Input() revenues: number;
  @Input() ytdactual: number;
  @Input() ytdbudget: number;
  @Input() annualbudget: number;
  @Input() colors: string[];
  
  chartSize: number = 40;
  YTDPercentage: number = 0;
  year = moment().year();

  constructor(public translateService: TranslateService) {}

  ngOnChanges(): void {
    this.YTDPercentage = this.ytdactual && this.ytdbudget ? this.ytdactual / this.ytdbudget : 0;

    if (this.chart) {
      this.chart.series[0].setData(this.calculateChartData(), true, false);
    }
  }

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private createChart(): void {
    const chartOptions: Highcharts.Options = {
      chart: {
        type: 'pie',
        height: this.chartSize,
        width: this.chartSize,
        spacingLeft: 0,
        spacingRight: 0,
        spacingTop: 0,
        spacingBottom: 0,
        backgroundColor: 'transparent',
        margin: [0, 0, 0, 0],
        renderTo: this.chartId
      },
      title: {
        text: null
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        pie: {
          size: this.chartSize - 1,
          innerSize: '50%', // donut chart
          dataLabels: {
            enabled: false
          },
          borderWidth: 0,
          borderRadius: 0,
          startAngle: -90, // Start from the left
          endAngle: 90 // Go half circle
        },
        series: {
          enableMouseTracking: false
        }
      },
      series: [{
        type: 'pie',
        data: this.calculateChartData(),
      }],
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      }
    };

    this.chart = Highcharts.chart(this.chartId, chartOptions);
  }

  public calculateChartData() {
    const RED = 2;
    const [primary, secondary] = this.colors;

    const red = { y: RED, color: 'red' };
    const segment = (y: number, color: string) => ({ y, color });

    const patterns = {
      noRevenue: {
        fullBudget: () => [red, segment(100 - RED, primary)],
        partialBudget: () => [
          red,
          segment(this.budget, primary),
          segment(100 - this.budget - RED, secondary),
        ],
        zeroBudget: () => [red, segment(100 - RED, secondary)],
      },
      fullRevenue: {
        fullBudget: () => [segment(100 - RED, primary), red],
        partialBudget: () => [
          segment(this.budget, primary),
          segment(100 - this.budget - RED, secondary),
          red,
        ],
        zeroBudget: () => [red, segment(100 - RED, secondary)],
      },
      partialRevenue: {
        budgetLess: () => [
          segment(this.budget, primary),
          segment(this.revenues - this.budget - 1, secondary),
          red,
          segment(100 - this.revenues - 1, secondary),
        ],
        budgetEqual: () => [
          segment(this.budget, primary),
          red,
          segment(100 - this.budget - RED, secondary),
        ],
        budgetMore: () =>
          this.budget < 100
            ? [
                segment(this.budget, primary),
                red,
                segment(this.revenues - this.budget - 1, primary),
                segment(100 - this.revenues - 1, secondary),
              ]
            : [
                segment(this.budget, primary),
                red,
                segment(100 - this.revenues - RED, primary),
              ],
      },
    };

    if (!this.revenues) {
      if (this.budget >= 100) return patterns.noRevenue.fullBudget();
      if (this.budget > 0) return patterns.noRevenue.partialBudget();
      return patterns.noRevenue.zeroBudget();
    }

    if (this.revenues === 100) {
      if (this.budget >= 100) return patterns.fullRevenue.fullBudget();
      if (this.budget > 0) return patterns.fullRevenue.partialBudget();
      return patterns.fullRevenue.zeroBudget();
    }

    if (this.budget < this.revenues) return patterns.partialRevenue.budgetLess();
    if (this.budget === this.revenues) return patterns.partialRevenue.budgetEqual();
    return patterns.partialRevenue.budgetMore();
  }

}
